import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip } from "@/components/chart";
import ChartTemplate from "@/components/chart-template";
import { ConversionAvailabilityRow } from "@/http/api-types";
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { calculateAvailabilityStats } from "../performance-calculations";
import { CustomTooltipContent } from "./chart-utils";

type ChartData = {
  dateTime: string;
  withBidTs: number;
  withoutBidTs: number;
  percentage: number | undefined;
};

type ConversionAvailabilityChartProps = {
  data: ConversionAvailabilityRow[];
  type: "primary" | "secondary";
};

const primaryChartConfig = {
  withBidTs: {
    label: "With Bid ID",
    color: "hsl(var(--chart-1))",
  },
  withoutBidTs: {
    label: "Without Bid ID",
    color: "hsl(var(--chart-2))",
  },
  percentage: {
    label: "% Bid ID Availability",
    color: "hsl(var(--chart-3))",
  },
} satisfies ChartConfig;

const secondaryChartConfig = {
  withBidTs: {
    label: "With Bid ID",
    color: "hsl(var(--chart-1))",
  },
  withoutBidTs: {
    label: "Without Bid ID",
    color: "hsl(var(--chart-2))",
  },
  percentage: {
    label: "% Bid ID Availability",
    color: "hsl(var(--chart-3))",
  },
} satisfies ChartConfig;

const calculatePercentage = (withBidTs: number, total: number) => {
  return total > 0 ? (withBidTs / total) * 100 : total === 0 ? undefined : 0;
};

function processChartData(data: ConversionAvailabilityRow[], type: "primary" | "secondary"): ChartData[] {
  const dailyAggregated = data.reduce((acc: { [key: string]: any }, row) => {
    const date = new Date(row.bid_ts_hour).toISOString().split("T")[0];

    if (!acc[date]) {
      acc[date] = {
        dateTime: date,
        ap_conversions_total: 0,
        secondary_ap_conversions_total: 0,
        ap_conversions_with_bid_ts: 0,
        secondary_ap_conversions_with_bid_ts: 0,
      };
    }

    acc[date].ap_conversions_total += row.ap_conversions_total;
    acc[date].secondary_ap_conversions_total += row.secondary_ap_conversions_total;
    acc[date].ap_conversions_with_bid_ts += row.ap_conversions_with_bid_ts;
    acc[date].secondary_ap_conversions_with_bid_ts += row.secondary_ap_conversions_with_bid_ts;

    return acc;
  }, {});

  return Object.values(dailyAggregated)
    .map((day: any) => {
      const withBidTs = type === "primary" ? day.ap_conversions_with_bid_ts : day.secondary_ap_conversions_with_bid_ts;
      const total = type === "primary" ? day.ap_conversions_total : day.secondary_ap_conversions_total;
      const withoutBidTs = total - withBidTs;

      return {
        dateTime: day.dateTime,
        withBidTs,
        withoutBidTs,
        percentage: calculatePercentage(withBidTs, total),
      };
    })
    .sort((a, b) => a.dateTime.localeCompare(b.dateTime));
}

function ConversionAvailabilityChart({ data, type }: ConversionAvailabilityChartProps) {
  const chartData = processChartData(data, type);
  const config = type === "primary" ? primaryChartConfig : secondaryChartConfig;

  const labelFormatter = (value: string) => {
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const availabilityStats = calculateAvailabilityStats(data, type);

  const chartContent = (
    <>
      <ChartContainer config={config}>
        <ComposedChart data={chartData}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="dateTime" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={labelFormatter} />
          <YAxis
            yAxisId="count"
            orientation="left"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value: number) => value.toLocaleString()}
          />
          <YAxis
            yAxisId="percentage"
            orientation="right"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={[0, 100]}
            tickFormatter={(value: number) => `${value.toFixed(0)}%`}
          />
          <ChartTooltip
            cursor={false}
            content={<CustomTooltipContent labelFormatter={labelFormatter} config={config} />}
          />
          <Area
            yAxisId="count"
            dataKey="withBidTs"
            stackId="1"
            stroke="var(--color-withBidTs)"
            fill="var(--color-withBidTs)"
            fillOpacity={0.5}
            strokeWidth={1}
            strokeOpacity={0.8}
          />
          <Area
            yAxisId="count"
            dataKey="withoutBidTs"
            stackId="1"
            stroke="var(--color-withoutBidTs)"
            fill="var(--color-withoutBidTs)"
            fillOpacity={0.4}
            strokeWidth={1}
            strokeOpacity={0.7}
          />
          <Line
            yAxisId="percentage"
            dataKey="percentage"
            type="linear"
            stroke="var(--color-percentage)"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
          <ChartLegend content={<ChartLegendContent />} />
        </ComposedChart>
      </ChartContainer>
      <div className="mt-4 px-2">
        <p className="text-xs text-muted-foreground">
          <strong>Note:</strong> Gaps in % line indicate no events occurred (vs 0% which means events occurred but
          without the Bid Context)
        </p>
      </div>
    </>
  );

  return (
    <ChartTemplate
      chart={chartContent}
      table={null}
      title={`${type === "primary" ? "AP" : "2nd AP"} Conversions with Bid Context`}
      description={`Bid context availability for ${type === "primary" ? "AP" : "2nd AP"} conversions`}
      summaryMetric={`${availabilityStats.avgAvailability.toFixed(1)}%`}
      summaryMetricLabel="Bid Context Availability"
      timeSplittable={false}
    />
  );
}

export default ConversionAvailabilityChart;
